import { 
  LegalPageLayout, 
  LegalSection, 
  LegalSubsection,
  LegalParagraph,
  LegalList,
  LegalListItem
} from './legal-page-layout';

interface TermsOfServiceProps {
  onNavigateBack?: () => void;
}

/**
 * Terms of Service page
 * Structure follows enterprise SaaS best practices
 * Placeholder content can be replaced by legal team
 */
export function TermsOfService({ onNavigateBack }: TermsOfServiceProps) {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="11-Feb-2026"
      onNavigateBack={onNavigateBack}
    >
      {/* Introduction */}
      <LegalSection title="1. Introduction">
        <LegalParagraph>
          Welcome to Upmode AI ("Company", "we", "our", "us"). These Terms of Service ("Terms") govern your access to and use of Upmode AI's platform, services, and related products (collectively, the "Service").
        </LegalParagraph>
        <LegalParagraph>
          By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add jurisdiction-specific language and definitions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Account Registration */}
      <LegalSection title="2. Account Registration and Security">
        <LegalParagraph>
          To access certain features of the Service, you must register for an account. You agree to:
        </LegalParagraph>
        <LegalList>
          <LegalListItem>Provide accurate, current, and complete information during registration</LegalListItem>
          <LegalListItem>Maintain and promptly update your account information</LegalListItem>
          <LegalListItem>Maintain the security of your password and account</LegalListItem>
          <LegalListItem>Accept responsibility for all activities that occur under your account</LegalListItem>
          <LegalListItem>Notify us immediately of any unauthorized access or security breach</LegalListItem>
        </LegalList>
        <LegalParagraph>
          <em>[Legal team: Add specific security requirements, age restrictions, and entity account provisions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Acceptable Use */}
      <LegalSection title="3. Acceptable Use">
        <LegalParagraph>
          You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
        </LegalParagraph>
        <LegalList>
          <LegalListItem>Violate any applicable laws or regulations</LegalListItem>
          <LegalListItem>Infringe upon or violate the intellectual property rights of others</LegalListItem>
          <LegalListItem>Upload or transmit viruses, malware, or other malicious code</LegalListItem>
          <LegalListItem>Attempt to gain unauthorized access to the Service or related systems</LegalListItem>
          <LegalListItem>Interfere with or disrupt the Service or servers</LegalListItem>
          <LegalListItem>Use the Service to harass, abuse, or harm others</LegalListItem>
          <LegalListItem>Reverse engineer, decompile, or disassemble the Service</LegalListItem>
        </LegalList>
        <LegalParagraph>
          <em>[Legal team: Add platform-specific restrictions and AI usage guidelines]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Service and Data */}
      <LegalSection title="4. Service Description and Data">
        <LegalSubsection title="4.1 Service Provision">
          <LegalParagraph>
            Upmode AI provides an AI perception optimization platform that analyzes how artificial intelligence models perceive and represent your brand. The Service includes brand monitoring, cognitive analysis, optimization recommendations, and related features.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add detailed service description, SLA terms, and availability disclaimers]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="4.2 Your Data">
          <LegalParagraph>
            You retain all rights to the data, content, and materials you submit to the Service ("Customer Data"). By submitting Customer Data, you grant us a limited license to use, process, and analyze such data solely to provide and improve the Service.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add data ownership, licensing scope, and data processing terms]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="4.3 AI-Generated Content">
          <LegalParagraph>
            The Service may generate insights, recommendations, and other content using artificial intelligence. While we strive for accuracy, AI-generated content should be reviewed and validated before use in business decisions.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add AI liability limitations and accuracy disclaimers]</em>
          </LegalParagraph>
        </LegalSubsection>
      </LegalSection>

      {/* Intellectual Property */}
      <LegalSection title="5. Intellectual Property Rights">
        <LegalParagraph>
          The Service and its original content, features, and functionality are owned by Upmode AI and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
        </LegalParagraph>
        <LegalParagraph>
          Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add detailed IP provisions, trademark guidelines, and license grants]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Payment Terms */}
      <LegalSection title="6. Payment and Subscription">
        <LegalSubsection title="6.1 Fees">
          <LegalParagraph>
            Access to certain features of the Service requires payment of fees. All fees are non-refundable unless otherwise specified in your subscription agreement.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add pricing structure, billing cycles, and payment processing terms]</em>
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="6.2 Subscription Terms">
          <LegalParagraph>
            Subscriptions automatically renew at the end of each billing period unless cancelled. You may cancel your subscription at any time through your account settings.
          </LegalParagraph>
          <LegalParagraph>
            <em>[Legal team: Add auto-renewal terms, cancellation policy, and refund provisions]</em>
          </LegalParagraph>
        </LegalSubsection>
      </LegalSection>

      {/* Termination */}
      <LegalSection title="7. Termination">
        <LegalParagraph>
          We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms.
        </LegalParagraph>
        <LegalParagraph>
          Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may do so through your account settings.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add data retention after termination, transition assistance, and survival provisions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Warranties and Disclaimers */}
      <LegalSection title="8. Warranties and Disclaimers">
        <LegalParagraph>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </LegalParagraph>
        <LegalParagraph>
          We do not warrant that the Service will be uninterrupted, secure, or error-free, or that defects will be corrected.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add jurisdiction-specific warranty disclaimers and limitations]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Limitation of Liability */}
      <LegalSection title="9. Limitation of Liability">
        <LegalParagraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, UPMODE AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add cap on liability, carve-outs for gross negligence, and jurisdiction-specific limitations]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Indemnification */}
      <LegalSection title="10. Indemnification">
        <LegalParagraph>
          You agree to indemnify, defend, and hold harmless Upmode AI and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of the Service or violation of these Terms.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add indemnification scope, notice requirements, and defense control provisions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Dispute Resolution */}
      <LegalSection title="11. Dispute Resolution">
        <LegalParagraph>
          Any disputes arising from these Terms or the Service will be resolved through binding arbitration in accordance with the rules of [Arbitration Organization].
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add arbitration clause, class action waiver, governing law, and venue provisions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Changes to Terms */}
      <LegalSection title="12. Changes to Terms">
        <LegalParagraph>
          We reserve the right to modify these Terms at any time. We will notify you of material changes by email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
        </LegalParagraph>
        <LegalParagraph>
          <em>[Legal team: Add notice period, version history, and material change definitions]</em>
        </LegalParagraph>
      </LegalSection>

      {/* General Provisions */}
      <LegalSection title="13. General Provisions">
        <LegalSubsection title="13.1 Severability">
          <LegalParagraph>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="13.2 Entire Agreement">
          <LegalParagraph>
            These Terms constitute the entire agreement between you and Upmode AI regarding the Service and supersede all prior agreements.
          </LegalParagraph>
        </LegalSubsection>

        <LegalSubsection title="13.3 Waiver">
          <LegalParagraph>
            No waiver of any provision of these Terms shall be deemed a further or continuing waiver of such provision or any other provision.
          </LegalParagraph>
        </LegalSubsection>

        <LegalParagraph>
          <em>[Legal team: Add assignment, force majeure, notices, and other standard clauses]</em>
        </LegalParagraph>
      </LegalSection>

      {/* Contact */}
      <LegalSection title="14. Contact Information">
        <LegalParagraph>
          For questions about these Terms, please contact us at:
        </LegalParagraph>
        <LegalParagraph>
          <strong>Upmode AI</strong><br />
          Email: legal@upmode.ai<br />
          <em>[Legal team: Add physical address and additional contact methods]</em>
        </LegalParagraph>
      </LegalSection>
    </LegalPageLayout>
  );
}
